import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownToLine, ArrowUpFromLine, ChevronRight, Gift, Globe, Headphones, History, RefreshCw, Sparkles, Users, Zap, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { money, dateTime } from '@/lib/format';
import { openExternal } from '@/lib/telegram';
import { useI18n } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { BrandMark, MethodIcon, StatusPill, Spinner } from '@/components/ui';
import type { Tx } from '@/types/api';

function RegisterHero() {
    const { t, lang } = useI18n();
    const { me } = useAuth();
    const navigate = useNavigate();
    return (
        <div className="flex flex-col items-center text-center py-6 gap-6">
            <div className="relative">
                <div className="absolute inset-0 -m-10 rounded-full bg-brand-400/15 blur-3xl" />
                <div className="relative animate-float"><BrandMark size="xl" glow /></div>
            </div>
            <div className="space-y-2 max-w-xs">
                <h1 className="text-2xl font-extrabold font-display">
                    <span className="text-lime">{t('home.hello')}</span>{me?.user.firstName ? <span> {me.user.firstName}</span> : null}
                </h1>
                <p className="text-sm text-ink-muted leading-relaxed">{t('home.registerText', { brand: me?.brand.name || 'Bora4Bet' })}</p>
            </div>
            {me?.gift && (
                <div className="card-lime w-full max-w-xs flex items-center gap-3 py-3 text-left">
                    <Gift className="w-7 h-7 text-brand-300 shrink-0" />
                    <div className="text-sm"><span className="font-bold text-lime">{t('home.giftTitle', { amount: money(me.gift.amount, me.gift.currency, lang) })}</span><br /><span className="text-ink-muted text-xs">{t('home.giftText')}</span></div>
                </div>
            )}
            <button className="btn-primary w-full max-w-xs text-base" onClick={() => navigate('/register')}>
                <Sparkles className="w-4 h-4" /> {t('home.createAccount')}
            </button>
            <div className="w-full grid grid-cols-3 gap-2 pt-2">
                {[{ icon: Zap, label: t('home.hintPix') }, { icon: ShieldCheck, label: t('home.hintSafe') }, { icon: Gift, label: t('home.hintBonus') }].map(h => (
                    <div key={h.label} className="card flex flex-col items-center gap-2 py-3 bg-white/[.02]">
                        <div className="p-2 rounded-lg bg-brand-400/10 text-brand-300 border border-brand-400/20"><h.icon className="w-4 h-4" /></div>
                        <span className="text-[11px] text-ink-muted">{h.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function BalanceCard() {
    const { t, lang } = useI18n();
    const { me } = useAuth();
    const q = useQuery({
        queryKey: ['balance'],
        queryFn: async () => (await api.get<{ balance: number; currency: string }>('/balance')).data,
        staleTime: 20_000
    });
    return (
        <section className="relative overflow-hidden rounded-2xl p-5 border border-brand-400/30"
                 style={{ background: 'linear-gradient(135deg, #16240f 0%, #0d150d 55%, #0a1110 100%)', boxShadow: '0 20px 50px -20px rgba(158,240,31,.35), inset 0 1px 0 rgba(255,255,255,.06)' }}>
            <div aria-hidden className="absolute -top-20 -left-20 w-56 h-56 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(158,240,31,.32), transparent 70%)' }} />
            <div aria-hidden className="absolute -bottom-24 -right-16 w-56 h-56 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(46,230,214,.18), transparent 70%)' }} />
            <div aria-hidden className="absolute inset-0 pointer-events-none opacity-40"
                 style={{ background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,.08) 50%, transparent 70%)', backgroundSize: '200% 100%', animation: 'shimmer 6s linear infinite' }} />
            <div className="relative flex items-start justify-between">
                <div>
                    <p className="text-[11px] text-white/60 tracking-widest uppercase">{t('home.welcomeBack')}</p>
                    <h2 className="text-lg font-bold text-white mt-0.5">{me?.account?.username || me?.user.firstName}</h2>
                    <p className="text-[11px] text-white/50 mt-0.5">ID {me?.account?.playerId}</p>
                </div>
                <button className="p-2 rounded-xl bg-white/5 border border-white/10 active:scale-95" onClick={() => q.refetch()} aria-label={t('common.refresh')}>
                    {q.isFetching ? <Spinner className="w-4 h-4 text-brand-300" /> : <RefreshCw className="w-4 h-4 text-brand-300" />}
                </button>
            </div>
            <div className="relative mt-5">
                <p className="text-xs text-white/60">{t('home.balance')}</p>
                {q.isError ? (
                    <p className="text-sm text-amber-200 mt-1">{t('errors.balance_unavailable')}</p>
                ) : (
                    <p className="text-4xl font-black font-display tracking-tight text-lime mt-1">
                        {q.data ? money(q.data.balance, q.data.currency, lang) : <span className="inline-block skeleton w-40 h-9 align-middle" />}
                    </p>
                )}
            </div>
        </section>
    );
}

function RecentActivity() {
    const { t, lang } = useI18n();
    const navigate = useNavigate();
    const q = useQuery({
        queryKey: ['transactions', 'recent'],
        queryFn: async () => (await api.get<{ total: number; items: Tx[] }>('/transactions', { params: { limit: 3 } })).data,
        staleTime: 15_000
    });
    if (!q.data || !q.data.items.length) return null;
    return (
        <section className="space-y-2">
            <div className="flex items-center justify-between">
                <h3 className="section-title">{t('home.recent')}</h3>
                <button className="text-xs text-brand-300" onClick={() => navigate('/history')}>{t('home.seeAll')}</button>
            </div>
            {q.data.items.map(tx => (
                <button key={tx.operationId} className="card card-tap w-full flex items-center gap-3 text-left py-3" onClick={() => navigate(`/tx/${tx.operationId}`)}>
                    <MethodIcon kind={tx.kind} size="sm" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{t(`tx.type.${tx.type}`)} · {tx.method}</div>
                        <div className="text-[11px] text-ink-muted">{dateTime(tx.createdAt, lang)}</div>
                    </div>
                    <div className="text-right space-y-1">
                        <div className="text-sm font-bold">{money(tx.amount, tx.currency, lang)}</div>
                        <StatusPill status={tx.status} />
                    </div>
                </button>
            ))}
        </section>
    );
}

export function HomePage() {
    const { t } = useI18n();
    const { me } = useAuth();
    const navigate = useNavigate();
    if (!me?.registered) return <RegisterHero />;
    const tiles = [
        { icon: History, label: t('home.tiles.history'), go: () => navigate('/history') },
        { icon: Users, label: t('home.tiles.referrals'), go: () => navigate('/referrals') },
        { icon: Gift, label: t('home.tiles.giftCodes'), go: () => navigate('/gift-codes') },
        { icon: Headphones, label: t('home.tiles.support'), go: () => navigate('/support') }
    ];
    return (
        <div className="space-y-5">
            <BalanceCard />
            <div className="grid grid-cols-2 gap-3">
                <button className="btn-primary py-4 text-base" onClick={() => navigate('/deposit')}><ArrowDownToLine className="w-5 h-5" /> {t('nav.deposit')}</button>
                <button className="btn-secondary py-4 text-base" onClick={() => navigate('/withdraw')}><ArrowUpFromLine className="w-5 h-5" /> {t('nav.withdraw')}</button>
            </div>
            <div className="grid grid-cols-4 gap-2">
                {tiles.map(tile => (
                    <button key={tile.label} onClick={tile.go} className="card card-tap flex flex-col items-center gap-2 px-1 py-3">
                        <div className="p-2.5 rounded-xl bg-brand-400/10 text-brand-300 border border-brand-400/20"><tile.icon className="w-5 h-5" /></div>
                        <span className="text-[11px] text-ink-muted text-center leading-tight">{tile.label}</span>
                    </button>
                ))}
            </div>
            {me.brand.siteUrl && (
                <button className="card-lime w-full flex items-center gap-3 text-left active:scale-[.99] transition" onClick={() => openExternal(me.brand.siteUrl)}>
                    <div className="p-2.5 rounded-xl bg-brand-400/15 text-brand-300 border border-brand-400/30"><Globe className="w-5 h-5" /></div>
                    <div className="flex-1">
                        <div className="text-sm font-bold">{t('home.playNow')}</div>
                        <div className="text-[11px] text-ink-muted">{t('home.playNowHint')}</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-brand-300" />
                </button>
            )}
            <RecentActivity />
        </div>
    );
}
